export const SERVICE_PAGES = {
  "check-ups-and-cleans": {
    metaTitle: "Dental Check-up & Clean Canberra City | Glebe Park Dental",
    metaDesc:
      "Thorough, unhurried dental check-ups and cleans in Canberra City, next to Glebe Park. HICAPS on the spot, new patients welcome, no lecture guaranteed. Book online.",
    intro:
      "The check-up is the least glamorous thing we do and easily the most important. Catch a cavity when it's the size of a pinhead and it's a ten-minute filling. Leave it two years and you're looking at a root canal and a crown. Same tooth, wildly different afternoon.",
    goodFor: [
      "It's been more than six months (or, let's be honest, more than six years)",
      "You've moved to Canberra and need a new regular dentist",
      "Bleeding gums, bad breath, or a tooth that's started feeling 'different'",
      "You just want to know where you stand — no pressure to do anything",
    ],
    sections: [
      {
        h: "What actually happens",
        p: "We start with a chat — what's bugging you, what your history is, anything you're nervous about. Then Dr Gupta has a proper look at every tooth, your gums, your tongue and the soft tissue around your mouth (that last bit is a quick oral cancer screen — takes thirty seconds, worth doing). If something needs a closer look we'll take a digital X-ray, but only when there's a reason. Then it's on to the clean: scale off the tartar your toothbrush can't shift, polish, and a quick fluoride if it suits you.",
      },
      {
        h: "The bit most people like",
        p: "You get to see what we see. We put the intraoral camera photos up on the screen and go through them together, so when we say 'there's a small crack on the back molar' you can actually look at it rather than take our word for it. Then we talk through what needs doing now, what can wait, and what we'll just keep an eye on.",
      },
      {
        h: "How long and how often",
        p: "Most check-ups and cleans take 45 minutes to an hour. Six-monthly is the standard advice and it's a good default, but plenty of people with healthy mouths are fine on yearly visits, and some people with gum issues do better on three or four. We'll tell you honestly which camp you're in.",
      },
      {
        h: "Cost and health funds",
        p: "We claim through HICAPS on the spot, so if you've got extras cover you just pay the gap. Most funds cover two check-ups a year. If you're not insured, ask reception for the fee when you book — we'd rather you know upfront than get a surprise.",
      },
    ],
    faqs: [
      {
        q: "Will the clean hurt?",
        a: "Usually not. If you've got a lot of build-up or sensitive gums it can be a bit tender, and we'll go gently and use numbing gel if it helps. Tell us if anything's uncomfortable — we can always stop.",
      },
      {
        q: "Do I need X-rays every time?",
        a: "No. We take them when there's a clinical reason — usually every two to three years for a general check between the teeth, or sooner if something specific needs a look. Our X-rays are digital, so the dose is tiny.",
      },
      {
        q: "I'm embarrassed about my teeth. Is that a problem?",
        a: "Not even slightly. We've seen everything, and nobody here is going to make you feel bad. Come in, we'll start where you're at.",
      },
    ],
  },

  "same-day-crowns": {
    metaTitle: "Same-Day Ceramic Crowns Canberra | One Visit | Glebe Park Dental",
    metaDesc:
      "Broken or cracked tooth? Get a permanent ceramic crown designed, milled and fitted in one visit at Glebe Park Dental, Canberra City. No temporaries, no second appointment.",
    intro:
      "Crowns used to be a two-week saga: gooey impression, temporary crown, wait for the lab, come back, hope it fits. We do the whole thing in one appointment with a digital scanner and a milling machine in the practice. Walk in with a broken tooth, walk out with a permanent one.",
    goodFor: [
      "A tooth that's cracked, badly broken or worn down",
      "A big old filling that keeps failing",
      "Protecting a tooth after root canal treatment",
      "You can't (or really don't want to) come back for a second visit",
    ],
    sections: [
      {
        h: "How a same-day crown works",
        p: "We numb the tooth and shape it so the crown has something solid to sit on. Then instead of a tray of impression putty, we scan your tooth with a small wand — takes a couple of minutes and there's nothing to gag on. Dr Gupta designs the crown on screen while you watch (or scroll your phone, no judgement), and the milling machine carves it from a solid block of ceramic in about fifteen minutes. We check the fit and the shade, bond it in, and you're done.",
      },
      {
        h: "Why one visit beats two",
        p: "No temporary crown that pops off at dinner. No second round of anaesthetic. No repeat trip into the city. And because the scan is digital, the fit tends to be more precise than a putty impression that can distort on the way to the lab. The ceramic we use is strong and matches your natural tooth colour, so it doesn't look like a crown.",
      },
      {
        h: "How long it takes",
        p: "Set aside about two hours. There's a stretch in the middle where the crown is being milled and you're free to grab a coffee or catch up on emails. Most people are surprised how uneventful the whole thing is.",
      },
      {
        h: "Cost and health funds",
        p: "Crowns are a 'major dental' item on most health funds, so your rebate depends on your level of extras cover. We'll quote you before we start and swipe your card through HICAPS on the day so you only pay the gap.",
      },
    ],
    faqs: [
      {
        q: "How long will a ceramic crown last?",
        a: "With normal care, ten to fifteen years is typical and many go well beyond that. Brushing, flossing and not using your teeth as bottle openers all help.",
      },
      {
        q: "Will it look like a crown?",
        a: "It shouldn't. We match the shade to your neighbouring teeth and the ceramic has a natural translucency. Most people can't pick which tooth it is a few weeks later.",
      },
      {
        q: "Can every tooth be crowned in one visit?",
        a: "Almost all. Occasionally a tooth needs more work first (like a root canal) or a very specific cosmetic result for a front tooth, and we'll talk you through that upfront.",
      },
    ],
  },

  "dental-implants": {
    metaTitle: "Dental Implants Canberra City | Glebe Park Dental",
    metaDesc:
      "Replace a missing tooth with a dental implant at Glebe Park Dental, Canberra City. 3D-planned, gently placed, clearly priced. Free-of-jargon consults, next to Glebe Park.",
    intro:
      "A gap where a tooth used to be is more than a cosmetic thing. The bone underneath starts shrinking, the neighbours drift into the space, and you end up chewing on one side. An implant fixes all of that — it's the closest thing dentistry has to giving you your tooth back.",
    goodFor: [
      "A single missing tooth, front or back",
      "Several missing teeth you'd rather not bridge or wear a plate for",
      "A tooth that's past saving and needs to come out",
      "Loose dentures that you're sick of",
    ],
    sections: [
      {
        h: "What an implant actually is",
        p: "Three parts. A small titanium post that goes into the jawbone and acts as the root. A connector on top called an abutment. And a ceramic crown that's the bit you see and chew with. The titanium fuses with your bone over a few months, which is why implants feel solid in a way dentures never do.",
      },
      {
        h: "How the process runs",
        p: "First, a consult and a 3D scan so Dr Gupta can see exactly how much bone you've got and plan where the implant goes — no guesswork. Then the placement appointment, done here under local anaesthetic; most people say it's easier than having the tooth out. Then a healing period of roughly three to six months while the bone knits around the post. Finally, we scan and fit your permanent crown. Throughout, you'll have something to smile with — nobody walks around with a gap.",
      },
      {
        h: "What it feels like afterwards",
        p: "Like a tooth. You bite into apples, you eat steak, you floss around it like any other tooth. Because it's anchored in bone, it also stops the bone loss that happens under a gap or a bridge. Look after it and it can last decades.",
      },
      {
        h: "Cost and health funds",
        p: "Implants are a genuine investment, and we'll give you a full itemised quote at your consult covering every stage, so there are no surprise add-ons later. Health funds with top-level extras often contribute; we'll help you work out what yours covers. Payment can be spread across the stages.",
      },
    ],
    faqs: [
      {
        q: "Does getting an implant hurt?",
        a: "The procedure itself is done under local anaesthetic and most patients are surprised how little they feel. Expect some soreness for a few days afterwards, usually managed with ordinary pain relief.",
      },
      {
        q: "Am I too old for an implant?",
        a: "Almost certainly not — there's no upper age limit. What matters is healthy gums, enough bone and good general health. Smoking and uncontrolled diabetes make things harder, and we'll be upfront about that.",
      },
      {
        q: "What if I don't have enough bone?",
        a: "It's common, especially if a tooth's been missing a while. Often a small bone graft at the time of placement solves it. The 3D scan tells us before we start.",
      },
    ],
  },

  "smile-makeovers": {
    metaTitle: "Smile Makeover Canberra | Veneers & Cosmetic Dentistry | Glebe Park Dental",
    metaDesc:
      "Smile makeovers in Canberra City that still look like you. Veneers, whitening, bonding and alignment planned together by Dr Avantica Gupta. Preview before you commit.",
    intro:
      "A smile makeover isn't a single procedure — it's a plan. We start with what bugs you about your smile (and what you like, because that matters too), then work out the simplest combination of treatments to get you there. The goal is a smile that looks natural in photos and unmistakably yours. Not a row of piano keys.",
    goodFor: [
      "Chipped, worn or uneven front teeth",
      "Gaps, crowding or one tooth that sits differently from the rest",
      "Old discoloured fillings or bonding on front teeth",
      "A big event coming up — wedding, milestone birthday, new job",
    ],
    sections: [
      {
        h: "Start with a conversation, not a sales pitch",
        p: "Your first visit is a consult. Bring photos of smiles you like (and ones you don't). Dr Gupta will look at your tooth shape, shade, gum line and how your lips move when you talk, and then talk you through what's realistic. Sometimes the answer is two veneers and some whitening. Sometimes it's a bit of bonding and nothing else. We'll always show you the least invasive route first.",
      },
      {
        h: "The tools we mix and match",
        p: "Porcelain veneers for reshaping and colour on front teeth. Composite bonding for small chips and gaps in a single visit. Professional whitening to brighten the base shade. Short-term braces or aligners if alignment is the real issue. Gum contouring if your smile shows too much gum. Each has its place; the skill is in choosing the right combination.",
      },
      {
        h: "See it before you commit",
        p: "For veneer cases we make a mock-up — a temporary version placed over your own teeth — so you can see and feel the shape in the mirror before anything permanent happens. If you want them a touch shorter or more rounded, we change it then. No surprises at the end.",
      },
      {
        h: "Timing and cost",
        p: "Most makeovers run two to four visits over a few weeks. We'll quote each part separately so you can see exactly where the money goes and choose to stage it if that suits your budget. Cosmetic work generally isn't covered by health funds, though some components (like a crown) may be.",
      },
    ],
    faqs: [
      {
        q: "Will my teeth look fake?",
        a: "Not if we do our job properly. We match shade, translucency and shape to your face — the best cosmetic dentistry is the kind nobody notices.",
      },
      {
        q: "Do veneers ruin your teeth?",
        a: "Modern veneers need only a very thin layer of enamel removed — sometimes none. Bonding removes nothing. We'll always tell you exactly what's involved for your teeth.",
      },
      {
        q: "How long do veneers last?",
        a: "Porcelain veneers typically last ten to fifteen years or more. Composite bonding is less expensive but usually needs refreshing sooner, around five to seven years.",
      },
    ],
  },

  "teeth-whitening": {
    metaTitle: "Professional Teeth Whitening Canberra City | Glebe Park Dental",
    metaDesc:
      "Dentist-strength teeth whitening in Canberra City — in-chair in 90 minutes or custom take-home trays. Safe for enamel, gums protected, real results. Next to Glebe Park.",
    intro:
      "There's a big difference between what you can buy at the chemist and what we can use in the practice. Our gels are prescription strength and applied with your gums protected, so you get an actual visible change without the zingy sensitivity. We'll also check your teeth first — whitening over a cavity is a bad time for everyone.",
    goodFor: [
      "Coffee, tea or red wine stains that brushing won't shift",
      "Teeth that have yellowed a bit with age",
      "A brighter smile before a wedding, photos or an interview",
      "The finishing touch after braces or a new crown",
    ],
    sections: [
      {
        h: "In-chair whitening",
        p: "The fast option. We protect your gums and lips, apply a high-strength gel and activate it in cycles over about 60 to 90 minutes. You'll see the difference before you leave — typically several shades. Good if you want it done today.",
      },
      {
        h: "Take-home whitening",
        p: "We scan your teeth and make custom trays that fit like a glove, then send you home with professional-grade gel. You wear them for an hour or so a day (or overnight) for one to two weeks and brighten gradually. Gentler, more controllable, and you keep the trays for top-ups.",
      },
      {
        h: "What whitening can't do",
        p: "It won't change the colour of crowns, veneers or fillings — so if you've got those on front teeth we'll talk about matching. Grey staining from old trauma or certain antibiotics responds less than yellow staining. We'll tell you honestly what result to expect before you pay for anything.",
      },
      {
        h: "Cost and results",
        p: "Whitening is cosmetic, so health funds generally don't cover it. Results last one to three years depending on your coffee habit; a quick top-up with your take-home trays brings it straight back. Ask reception for current pricing when you book.",
      },
    ],
    faqs: [
      {
        q: "Does it damage enamel?",
        a: "Professional whitening done properly doesn't. The gel works on the stain molecules, not the tooth structure. Charcoal pastes and acidic 'natural' remedies, on the other hand, genuinely do wear enamel away.",
      },
      {
        q: "Will my teeth be sensitive?",
        a: "Some people get mild, short-lived sensitivity for a day or two. We use a desensitising gel and can adjust strength or timing for sensitive teeth.",
      },
      {
        q: "Can I whiten if I have fillings?",
        a: "Yes, though the fillings won't change colour. If they're on front teeth and become noticeable, they can be replaced to match your new shade.",
      },
    ],
  },

  "clear-aligners": {
    metaTitle: "Clear Aligners & Invisalign Canberra City | Free Consult | Glebe Park Dental",
    metaDesc:
      "Straighten your teeth with clear aligners (Invisalign) at Glebe Park Dental, Canberra City. Near-invisible, removable, digitally planned. Free aligner consult for new patients.",
    intro:
      "Clear aligners are the grown-up way to straighten teeth: a series of thin, custom-made trays that nudge your teeth into place a fraction at a time. No brackets, no wires, and most people won't notice you're wearing them. You take them out to eat, brush and for the odd important photo. Your first consult with us is free.",
    goodFor: [
      "Crowded, gappy or crooked teeth you've always meant to fix",
      "Teeth that have drifted since you stopped wearing a teenage retainer",
      "Adults and teens who don't want visible braces at work or school",
      "Tidying alignment before veneers or whitening so the result lasts",
    ],
    sections: [
      {
        h: "How clear aligners work",
        p: "We scan your teeth digitally (no putty impressions) and plan every movement on screen, so you can see a preview of your finished smile before anything starts. A series of aligners is then made to that plan. You wear each set for about 20–22 hours a day, swapping to the next one every week or two, and pop in every six to eight weeks so we can check progress. Invisalign is the best-known brand; we'll recommend the system that suits your case.",
      },
      {
        h: "What it's actually like to wear them",
        p: "Snug for the first day or two of each new set, then you forget they're in. They're clear, so they're very hard to spot. You take them out to eat and drink anything other than water, brush, and put them back in — which is also why a lot of people end up snacking less. Speech is normal after a day. The main job is remembering to wear them; the aligners do the rest.",
      },
      {
        h: "How long it takes",
        p: "Simple cases — a bit of front-tooth crowding or a gap — can finish in six months or so. More involved cases run 12 to 18 months. At your free consult we'll tell you honestly where you sit and, if aligners aren't the right tool for your bite, we'll say so rather than sell you a plan that won't get there.",
      },
      {
        h: "Cost, health funds and the free consult",
        p: "Your first consultation is free for new patients — it covers the clinical assessment; any X-rays or scans needed are charged separately. After that you'll get a fixed quote for the whole treatment including refinements and your final retainers, with the option to spread payments across the treatment. Health funds with orthodontic extras often contribute.",
      },
    ],
    faqs: [
      {
        q: "Am I too old for aligners?",
        a: "No. Teeth move at any age and most of our aligner patients are adults in their 30s to 60s. Healthy gums matter more than age.",
      },
      {
        q: "Do aligners hurt?",
        a: "You'll feel pressure for a day or two each time you change to a new set — that's the teeth moving. It's mild, and most people manage without any pain relief at all.",
      },
      {
        q: "What happens when I finish?",
        a: "You'll get retainers to wear at night so your teeth stay where we've put them. Teeth have long memories; the retainer is the cheapest insurance you'll ever buy.",
      },
    ],
  },
  "root-canal-therapy": {
    metaTitle: "Root Canal Treatment Canberra City | Gentle Endodontics | Glebe Park Dental",
    metaDesc:
      "Toothache? Root canal treatment at Glebe Park Dental, Canberra City stops the pain and saves your tooth. Thorough numbing, no rush, same-day emergency slots where possible.",
    intro:
      "Root canals have a terrible reputation they no longer deserve. The toothache that brings you in is the painful part — the treatment is what makes it stop. Most patients tell us afterwards it felt like a long filling, and they're relieved to keep their own tooth rather than lose it.",
    goodFor: [
      "A deep, throbbing toothache, especially one that wakes you at night",
      "A tooth that's very sensitive to hot and stays sore for ages",
      "Swelling or a pimple-like bump on the gum near a tooth",
      "A tooth that's gone grey or darker than its neighbours",
    ],
    sections: [
      {
        h: "What's actually going on in there",
        p: "Every tooth has a nerve and blood supply running through its centre. When decay or a crack lets bacteria reach it, the nerve gets inflamed and then infected — that's the throbbing. Left alone, the infection spreads to the bone at the root tip and you get an abscess. A root canal removes the infected nerve, cleans and seals the space, and keeps the tooth.",
      },
      {
        h: "How the appointment goes",
        p: "We numb the tooth thoroughly — properly numb, not 'let's see how we go'. Dr Gupta makes a small opening, removes the infected tissue with fine instruments, disinfects the canals and fills them with a rubber-like material. Then a filling or, often, a crown to protect the tooth long term. Usually one or two visits of about an hour each.",
      },
      {
        h: "Afterwards",
        p: "The tooth may feel tender for a few days, especially when biting — that's the ligament around it calming down. Regular pain relief handles it. The deep ache should be gone almost immediately. A root-canalled tooth can last a lifetime if it's protected with a crown and looked after like any other tooth.",
      },
      {
        h: "Cost and health funds",
        p: "Root canal treatment sits under 'major dental' on most health funds. Price varies with the tooth — front teeth have one canal, molars have three or four — so we'll quote you after we've seen the X-ray. Splitting the cost across visits is fine.",
      },
    ],
    faqs: [
      {
        q: "Wouldn't it be easier to just pull the tooth?",
        a: "Quicker, maybe. But then you've got a gap that needs an implant or bridge to fill, which costs more and takes longer. Saving your own tooth is almost always the better outcome.",
      },
      {
        q: "Does it hurt?",
        a: "With modern anaesthetic, no — the goal is that you feel pressure and vibration, nothing more. If you're anxious, tell us. We'll go slowly and check in with you.",
      },
      {
        q: "I'm in pain now. How soon can I be seen?",
        a: "Ring us first thing on (02) 6295 9460. We keep room in the day for people in pain and we'll do our best to see you the same day.",
      },
    ],
  },

  "wisdom-teeth-removal": {
    metaTitle: "Wisdom Teeth Removal Canberra City | In-Chair | Glebe Park Dental",
    metaDesc:
      "Wisdom tooth assessment and gentle removal in the chair at Glebe Park Dental, Canberra City — no hospital needed for most cases. Straight advice on watch vs remove. Book online.",
    intro:
      "Not every wisdom tooth needs to come out — but the wrong one left too long is a painful mistake. We'll have a look, take an X-ray, and tell you straight: watch it, or take it out. If it's the latter, most can be removed right here under local anaesthetic, no hospital or general needed.",
    goodFor: [
      "Pain or swelling at the back of your jaw",
      "A wisdom tooth that's only partly through and traps food",
      "Repeated infections or a bad taste from the back of your mouth",
      "Your dentist has spotted an impacted tooth on an X-ray",
    ],
    sections: [
      {
        h: "When to watch and when to remove",
        p: "A wisdom tooth that's fully through, sits straight and can be cleaned properly can stay — we'll just keep an eye on it at check-ups. Removal comes into the conversation when a tooth is impacted (stuck against the tooth in front), partially erupted with a gum flap that traps food, repeatedly infected, or quietly damaging the molar next door. That last one is the sneaky one — it can progress painlessly until both teeth are in trouble.",
      },
      {
        h: "What removal is actually like",
        p: "We numb the area thoroughly and Dr Gupta removes the tooth — sometimes in one piece, sometimes in sections if it's impacted. You'll feel pressure but not pain. Most wisdom teeth take twenty to forty minutes. Then a few stitches if needed, gauze, clear aftercare instructions and our number in case you've got questions over the weekend.",
      },
      {
        h: "Recovery",
        p: "Plan for a few quiet days. Soft food, salt-water rinses from day two, no straws, no smoking, and don't poke at it. Swelling peaks around day two or three and settles from there. Most people are back to normal within a week. We'll book a quick follow-up to check healing.",
      },
      {
        h: "Cost and other extractions",
        p: "Price depends on how the tooth is sitting — a straightforward upper is very different from a deeply impacted lower — so we quote after the X-ray. Health funds contribute under general or major dental depending on the item. We also take out other teeth that are past saving, with the same gentle approach.",
      },
    ],
    faqs: [
      {
        q: "Do I need to go to hospital?",
        a: "For most wisdom teeth, no — they can be removed in the chair with local anaesthetic. Very deeply impacted teeth close to the nerve, or people who'd prefer to be asleep, may be better off with a specialist under general, and we'll refer you if that's the case.",
      },
      {
        q: "Do all four have to come out at once?",
        a: "Not necessarily. Some people prefer one side at a time so they can still chew comfortably. Some want it all done in one go. We'll work with what suits you.",
      },
      {
        q: "What's a dry socket?",
        a: "When the healing blood clot comes loose from the socket, usually from smoking, straws or vigorous rinsing too early. It's painful but fixable — ring us and we'll sort it quickly.",
      },
    ],
  },

  "childrens-dentistry": {
    metaTitle: "Kids' Dentist Canberra City | Child Dental Benefits Schedule | Glebe Park Dental",
    metaDesc:
      "Relaxed, pressure-free children's dentistry in Canberra City. First visits, check-ups, fissure seals and Child Dental Benefits Schedule (CDBS) bulk billing at Glebe Park Dental.",
    intro:
      "A child's first few dental visits set the tone for life, so we keep them short, light and pressure-free. They get a ride in the chair, we count their teeth together, they might get a go with the little mirror — and that's often it. If your kid leaves wanting to come back, that's a successful appointment.",
    goodFor: [
      "A first visit (around age one, or when the first teeth are through)",
      "Regular check-ups and cleans for school-aged kids",
      "Fissure sealants to protect new adult molars",
      "A nervous child who's had a rough experience elsewhere",
    ],
    sections: [
      {
        h: "What a first visit looks like",
        p: "Honestly, not much — and that's the point. Your child sits in the chair (or on your lap if they'd rather), we have a look at their teeth, count them, maybe take a photo with the camera to show them. No X-rays, no instruments they don't want, nobody saying 'be brave'. We chat with you about brushing, bottles, snacks and dummies while they explore. High-five at the end is mandatory.",
      },
      {
        h: "As they get older",
        p: "Check-ups every six months keep an eye on new teeth coming through, spot early decay while it's tiny, and catch bite or crowding issues early — sometimes a small intervention at eight saves big braces at fourteen. When the adult molars arrive (around six and twelve), fissure sealants are a quick, painless coating that stops decay getting into the grooves.",
      },
      {
        h: "Child Dental Benefits Schedule",
        p: "Many Canberra families are eligible for the CDBS, which covers over $1,000 of basic dental care per child over two years — check-ups, cleans, X-rays, fillings, fissure seals and more — bulk billed, so nothing out of pocket. Eligibility is tied to Family Tax Benefit Part A. Ask reception when you book and we'll check for you in a couple of minutes.",
      },
      {
        h: "For anxious kids (and anxious parents)",
        p: "If your child has had a bad experience before, tell us when you book. We'll take it slower, maybe do a 'just looking' visit first, and let them set the pace. Kids pick up on your nerves too, so if you're a bit wary of dentists yourself, it's fine to say so — we'll make it easy for both of you.",
      },
    ],
    faqs: [
      {
        q: "When should my child first see a dentist?",
        a: "The Australian Dental Association recommends around age one, or within six months of the first tooth appearing. Earlier than most people expect — but it's more about habits and familiarity than treatment.",
      },
      {
        q: "Are you bulk billing under the CDBS?",
        a: "Yes. If your child is eligible, covered services are bulk billed with no gap. We'll confirm eligibility before the appointment.",
      },
      {
        q: "Can I come in with my child?",
        a: "Of course. For little ones we'd encourage it. Older kids sometimes do better solo — we'll follow their lead and yours.",
      },
    ],
  },

  "conscious-sedation": {
    metaTitle: "Sedation Dentistry Canberra City | Happy Gas & Oral Sedation | Glebe Park Dental",
    metaDesc:
      "Anxious about the dentist? Glebe Park Dental in Canberra City offers conscious sedation — nitrous oxide (happy gas) and oral sedation — for calm, comfortable treatment. Book online.",
    intro:
      "Plenty of people put off the dentist for years because of nerves, a bad experience as a kid, or a gag reflex that makes everything a battle. Conscious sedation takes the edge off. You stay awake and in control, but relaxed enough that the appointment just… happens. No judgement, no white knuckles.",
    goodFor: [
      "You've been avoiding the dentist because of anxiety",
      "A bad past experience that still makes you tense up",
      "A strong gag reflex, or trouble sitting still for longer procedures",
      "Bigger appointments — wisdom teeth, implants, several fillings in one go",
    ],
    sections: [
      {
        h: "Nitrous oxide (happy gas)",
        p: "The gentle option. You breathe a mix of nitrous oxide and oxygen through a small, soft nose mask. Within a few minutes you feel warm, relaxed and a little floaty — still awake, still able to talk to us, just not bothered by much. We adjust the level to suit you. When we're done, the gas is out of your system in minutes, so you can drive yourself home and get on with your day.",
      },
      {
        h: "Oral sedation",
        p: "For deeper relaxation, we prescribe a sedative tablet to take before your appointment. By the time you're in the chair you'll feel drowsy and calm, and most people remember very little afterwards. You'll be groggy for a few hours, so you'll need someone to bring you in and take you home, and you'll take the rest of the day off. We'll talk through your medical history first to make sure it's suitable.",
      },
      {
        h: "What sedation isn't",
        p: "It isn't a general anaesthetic — you're not put to sleep and there's no hospital involved. You can respond to us throughout, and we monitor you the whole time. For most nervous patients that's exactly the right balance: calm enough to get treatment done, safe enough to do it here in the practice.",
      },
      {
        h: "How to arrange it",
        p: "Just tell reception when you book that you'd like sedation, or mention it to Dr Gupta at your check-up. Happy gas can usually be added on the day; oral sedation needs a short conversation beforehand so we can prescribe and plan. Sedation is charged on top of the treatment itself — we'll give you the cost upfront.",
      },
    ],
    faqs: [
      {
        q: "Will I be asleep?",
        a: "No. Both options are conscious sedation — you're awake, relaxed and able to respond. Oral sedation makes you drowsy and many people remember little of the appointment, but you're never unconscious.",
      },
      {
        q: "Can I drive afterwards?",
        a: "After happy gas, yes — it clears within minutes. After oral sedation, no. You'll need someone to drive you home and you shouldn't drive or make important decisions for the rest of the day.",
      },
      {
        q: "Is sedation safe?",
        a: "Nitrous oxide has been used in dentistry for over a century and is extremely safe for most people. Oral sedation is also very safe when prescribed after a proper medical history, which is exactly why we do one first.",
      },
    ],
  },
};
